import { isNil } from 'lodash';
import { DECIMAL_PLACES } from 'common/config/app';
import {
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_ADJUSTMENT_SELECTIONS_VALUES,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_CALCULATIONS_COLUMNS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_METRICS,
} from 'pages/FundInvestments/common/constants';
import { PercentileSelection } from 'pages/FundInvestments/pages/FundInvestmentMarketAdjustment';
import {
  extractPercentileNumber,
  getArrayValue,
  getBooleanValue,
  getNumberValue,
  getObjectValue,
  parseCellValue,
} from 'utilities';
import {
  MarketAdjustmentFieldAttributesFields,
  MarketComparisonFieldAttributesFields,
  UpdateFieldValueParams,
} from './updateFieldValue.types';

const { percentile_selection_a: PERCENTILE_SELECTION_A, percentile_selection_b: PERCENTILE_SELECTION_B } =
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_CALCULATIONS_COLUMNS;

const updateFieldValue = (params: UpdateFieldValueParams): void => {
  const {
    category,
    cell,
    comparisonOrder,
    field,
    fieldAttributes,
    percentileSelection,
    setMarketAdjustment,
    setMarketComparisons,
    setSynchronizedMarketAdjustment,
  } = params;

  const { data, enabled } = getObjectValue(cell);
  const { capIqId } = getObjectValue(data);

  const { marketAdjustment: marketAdjustmentFieldAttributes, marketComparison: marketComparisonsFieldAttributes } =
    fieldAttributes;

  const { percentile_selection_a: percentileSelectionA } = getObjectValue(percentileSelection);

  let fieldDecimalPlaces = DECIMAL_PLACES;

  // Set the decimal places based on the category and field
  switch (category) {
    case 'market_adjustment':
    case 'percentile':
      if (field in marketAdjustmentFieldAttributes) {
        fieldDecimalPlaces = getNumberValue(
          marketAdjustmentFieldAttributes[field as MarketAdjustmentFieldAttributesFields]?.decimal_places
        );
      }
      break;

    case 'comparison':
      if (field in marketComparisonsFieldAttributes) {
        fieldDecimalPlaces = getNumberValue(
          marketComparisonsFieldAttributes[field as MarketComparisonFieldAttributesFields]?.decimal_places
        );
      }
      break;

    default:
      break;
  }

  const parsedCellValue = parseCellValue({ cell, fieldDecimalPlaces });

  // Update the state based on the category and field
  switch (category) {
    case 'market_adjustment':
      // Handle specific fields for market adjustment
      switch (field) {
        case 'as_of_date':
          setSynchronizedMarketAdjustment({
            asOfDate: parsedCellValue,
          });
          break;

        case 'metric_selection':
          setMarketAdjustment(prevState => ({
            ...getObjectValue(prevState),
            [field]: FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_METRICS[parsedCellValue],
          }));
          break;

        case 'adjustment_selection':
          if (FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_ADJUSTMENT_SELECTIONS_VALUES.includes(parsedCellValue)) {
            setMarketAdjustment(prevState => ({
              ...getObjectValue(prevState),
              [field]: parsedCellValue,
            }));
          } else {
            setMarketAdjustment(prevState => ({
              ...getObjectValue(prevState),
              [field]:
                extractPercentileNumber(parsedCellValue) === percentileSelectionA
                  ? PERCENTILE_SELECTION_A.title
                  : PERCENTILE_SELECTION_B.title,
            }));
          }
          break;

        default:
          setMarketAdjustment(prevState => ({
            ...getObjectValue(prevState),
            [field]: parsedCellValue,
          }));
          break;
      }
      break;

    case 'percentile':
      setSynchronizedMarketAdjustment({
        percentileSelection: {
          ...getObjectValue(percentileSelection),
          [field]: parsedCellValue,
        } as PercentileSelection,
      });
      break;

    case 'comparison':
      if (!isNil(capIqId)) {
        setMarketComparisons(prevState => {
          const updatedMarketComparisons = getArrayValue(prevState).map(comparison => {
            if (comparison?.cap_iq_id !== capIqId) {
              return comparison;
            }

            let updatedCellValue = parsedCellValue;

            // Handle specific fields for market comparison
            switch (field) {
              case 'enabled':
                updatedCellValue = getBooleanValue(enabled);
                break;

              case 'order':
                updatedCellValue = comparisonOrder;
                break;

              default:
                break;
            }

            return {
              ...comparison,
              [field]: updatedCellValue,
            };
          });

          return updatedMarketComparisons;
        });
      }
      break;

    default:
      break;
  }
};

export default updateFieldValue;
